import i18n from '@/lang'
export { timeJudge } from './timeJudge'
export { copyText } from './clipboard'
export { openUrlSmart, openDetailSmart } from './openUrlSmart'


// 判读是否在微信小程序中
const isInWxMini = () => {
  let isWxMini = ''
  const userAgent = navigator.userAgent.toLowerCase();
  if (/micromessenger/.test(userAgent)) {
    if (typeof window.__wxjs_environment !== 'undefined' && window.__wxjs_environment === 'miniprogram') {
      isWxMini = true
    } else {
      isWxMini = false
    }
  } else {
    isWxMini = false
  }
  return isWxMini
}
export const isInWxMiniProgram = isInWxMini
export const isWxMiniRes = isInWxMini()

const VISITOR_ID_KEY = '__visitor_id__'

/**
 * 生成 UUID v4 兜底标识
 */
function generateUUID() {
  return ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, c =>
    (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
  )
}

/**
 * 获取游客唯一身份标识
 * - 优先从 localStorage 读取缓存，命中则直接返回
 * - 未命中时通过 FingerprintJS 采集浏览器指纹，持久化到 localStorage
 * - 异常兜底：FingerprintJS 失败（含小程序 WebView）均降级为 UUID，不会卡住业务
 * @returns {Promise<string>} 游客唯一 ID
 */
export async function getVisitorId() {
  try {
    const cached = localStorage.getItem(VISITOR_ID_KEY)
    if (cached) return cached
  } catch (e) {
    // localStorage 不可用（如隐私模式），继续走生成逻辑
  }

  let id

  try {
    const fpPromise = import('@fingerprintjs/fingerprintjs').then(m => m.default.load())
    const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error('FingerprintJS timeout')), 5000))
    const fp = await Promise.race([fpPromise, timeoutPromise])
    const result = await fp.get()
    id = result.visitorId
  } catch (e) {
    console.warn('[getVisitorId] FingerprintJS 失败，降级为 UUID:', e.message)
    id = generateUUID()
  }

  try {
    localStorage.setItem(VISITOR_ID_KEY, id)
  } catch (e) {
    // 写入失败不影响返回
  }

  return id
}


/**
 * 判读是否为 PC 端内核浏览器
 * 综合 UA、触摸支持、屏幕尺寸过滤一切移动端设备（手机、平板）及微信小程序环境
 * @returns {boolean}
 */
const isInPCBrowser = () => {
  const userAgent = navigator.userAgent.toLowerCase()

  // 1. 明确移动端 UA 标识（覆盖手机 + 平板常规情况）
  const mobileAgents = [
    'mobile', 'android', 'iphone', 'ipod',
    'ipad', 'windows phone', 'blackberry', 'webos',
    'iemobile', 'opera mini', 'playbook', 'silk',
    'kindle', 'xoom', 'nexus 7', 'nexus 9', 'nexus 10',
    'sm-t', 'gt-p', 'huawei-m', 'lenovo-tab'
  ]
  const isMobileUA = mobileAgents.some(agent => userAgent.includes(agent))

  // 2. iPadOS 13+ 特殊处理：UA 为 Macintosh，但支持多点触控
  const isIpad = userAgent.includes('ipad') ||
    (userAgent.includes('macintosh') && navigator.maxTouchPoints > 1)

  // 3. 常见移动端 App webview（微信/企业微信/钉钉/支付宝/抖音/微博/QQ/飞书等）
  const appAgents = [
    'micromessenger', 'wxwork', 'dingtalk', 'alipayclient',
    'weibo', 'aweme', 'douyin', 'qq/', 'mqqbrowser',
    'lark', 'feishu', 'baidu', 'ucbrowser', 'quark'
  ]
  const isAppWebview = appAgents.some(agent => userAgent.includes(agent))

  // 4. Android 平板通用检测（部分平板 UA 不含 mobile）
  const isAndroidTablet = userAgent.includes('android') && !userAgent.includes('mobile')

  return !isMobileUA && !isIpad && !isAppWebview && !isAndroidTablet && !isWxMiniRes
}
export const isPCBrowserRes = isInPCBrowser()


//表单序列化
export const serialize = (data) => {
  let list = [];
  Object.keys(data).forEach((ele) => {
    list.push(`${ele}=${data[ele]}`);
  });
  return list.join("&");
};


/**
 * 日期格式化 + 星期
 * @param {string | Date} dateInput - 如 '2026-01-16'
 * @param {'MM.DD Week' | 'YYYY-MM-DD Week' | 'YYYY-MM-DD Week HH:mm' | 'DD/MM/YYYY Week' | 'DD/MM/YYYY Week HH:mm' | 'YYYY-MM-DD'} format
 * @returns {string}
 */
export function formatDateWithWeek( dateInput, format = 'MM.DD Week') {
  const date = new Date((dateInput || '').trim());
  if (isNaN(date.getTime())) return (dateInput || '').trim();

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  const hour = String(date.getHours()).padStart(2, '0');
  const minute = String(date.getMinutes()).padStart(2, '0');

  const locale = i18n.global.locale.value || 'cn';
  const weekMap = {
    en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    cn: ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
  }[locale];
  const week = weekMap[date.getDay()];

  // 英文月份简写
  const monthNamesEn = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthEn = monthNamesEn[date.getMonth()];

  // 是否包含时间信息（HH:mm 或 HH:mm:ss）
  const hasTime = typeof dateInput === 'string' && /\d{2}:\d{2}/.test(dateInput);

  // 英文环境下的格式映射
  const isEn = locale === 'en';

  switch (format) {
    case 'YYYY-MM-DD Week HH:mm':
      if (isEn) {
        return hasTime
          ? `${day} ${monthEn} ${year} ${week} ${hour}:${minute}`
          : `${day} ${monthEn} ${year} ${week}`;
      }
      return hasTime
        ? `${year}-${month}-${day} ${week} ${hour}:${minute}`
        : `${year}-${month}-${day} ${week}`;

    case 'YYYY-MM-DD Week':
      if (isEn) {
        return `${day} ${monthEn} ${year} ${week}`;
      }
      return `${year}-${month}-${day} ${week}`;

    case 'YYYY-MM-DD':
      if (isEn) {
        return `${day} ${monthEn} ${year}`;
      }
      return `${year}-${month}-${day}`;

    case 'MM.DD Week':
    default:
      if (isEn) {
        return `${day} ${monthEn} ${week}`;
      }
      return `${month}.${day} ${week}`;
  }
}



/**
 * 为 timeSlot=1 和 timeSlot=2 的第一条数据添加 label
 * 1 -> 上午
 * 2 -> 下午
 * @param {Array<Object>} list
 * @returns {Array<Object>}
 */
export function addTimeSlotLabel(list = []) {
  const t = i18n.global.t
  let hasMorning = false;
  let hasAfternoon = false;

  return list.map(item => {
    const newItem = { ...item };

    if (newItem.timeSlot === 1 && !hasMorning) {
      newItem.label = t('common.上午');
      hasMorning = true;
    }

    if (newItem.timeSlot === 2 && !hasAfternoon) {
      newItem.label = t('common.下午');
      hasAfternoon = true;
    }

    return newItem;
  });
}


/**
 * 字符串切割为数组
 * @param {string} str - 原始字符串
 * @author: BYM<yongming.bai@outlook.com>
 * @param {string | RegExp} separator - 分隔符，默认 ','
 * @returns {string[]}
 */
export function splitToArray(str = '', separator = ',') {
  if (typeof str !== 'string') return [];

  return str
    .split(separator)
    .map(item => item.trim())
    .filter(Boolean);
}

/**
 * 从展位号字符串中提取第一个展位号
 * boothNo 可能被 ',' 或 ';' 分割为多个展位号
 * @param {string} boothNo - 展位号字符串，如 'W1A018,W1A031' 或 'W1A018;W1A031'
 * @returns {string} 第一个展位号
 */
export function extractFirstBoothNo(boothNo = '') {
  if (typeof boothNo !== 'string' || !boothNo.trim()) return '';
  const cleaned = boothNo.replace(/[\r\n]+/g, '');
  const parts = cleaned.split(/[,;]/);
  const boot = parts
    .map(item => item.trim())          // 去空格
    .filter(Boolean)                  // 过滤空项
    .map(item => item.split('/')[0]); // 取斜杠前部分
  return boot
}

/**
 * 按 timeSlot 分类数据
 * timeSlot = 1 -> morning 上午
 * timeSlot = 2 -> afternoon 下午
 * @param {Array<Object>} list
 * @returns {{ morning: Object[], afternoon: Object[] }}
 */
export function classifyByTimeSlot(list = []) {
  return list.reduce(
    (result, item) => {
      if (item.timeSlot === 1) {
        result.morning.push(item);
      } else if (item.timeSlot === 2) {
        result.afternoon.push(item);
      }
      return result;
    },
    { morning: [], afternoon: [] }
  );
}


/**
 * 日期排序
 * @param {string[]} list
 * @returns {string[]}
 */
export function sortDateString(list = []) {
  return [...list].sort(
    (a, b) => new Date(a).getTime() - new Date(b).getTime()
  );
}

export function formatDateTime(dateInput) {
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return '';

  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const hour = String(date.getHours()).padStart(2, '0');
  const minute = String(date.getMinutes()).padStart(2, '0');
  const second = String(date.getSeconds()).padStart(2, '0');

  return `${year}/${month}/${day} ${hour}:${minute}:${second}`;
}

/**
 * 三方网址过滤处理
 * @author: BYM<yongming.bai@outlook.com>
 */
export function webViewUrlFilter (url, params = {}) {
  if (!url) return ''
  let result = url
    .replace(/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//, 'https://')
    .replace(/^\/\//, 'https://')
    .replace(/^(?!https?:\/\/)/, 'https://')

  const queryString = serialize(params)
  if (queryString) {
    const hashIndex = result.indexOf('#')
    if (hashIndex !== -1) {
      const beforeHash = result.slice(0, hashIndex)
      const hash = result.slice(hashIndex)
      const separator = beforeHash.includes('?') ? '&' : '?'
      result = beforeHash + separator + queryString + hash
    } else {
      const separator = result.includes('?') ? '&' : '?'
      result += separator + queryString
    }
  }

  return result
}


/**
 * 根据参数映射关系，将数据对象中的值拼接到URL上生成最终链接
 * @param {string} url - 原始URL，如 'https://www.baidu.com' 或 'https://www.baidu.com?lang=en'
 * @param {Object} paramMapping - 参数映射关系，key为拼接到URL上的参数名，value为数据对象中对应的字段名
 *   如 { name: 'name', token: 'access_token' }
 * @param {Object} data - 数据源对象，如 { name: '白先生', access_token: 'eyh587545.....' }
 * @returns {string} 拼接后的完整URL
 * @example
 *   buildUrl(
 *     'https://www.baidu.com?lang=en',
 *     { name: 'name', token: 'access_token' },
 *     { name: '白先生', access_token: 'eyh587545.....' }
 *   )
 *   // => 'https://www.baidu.com?lang=en&name=%E7%99%BD%E5%85%88%E7%94%9F&token=eyh587545.....'
 */
export function buildUrl(url, paramMapping = {}, data = {}) {
  if (!url) return ''

  const parts = []
  Object.entries(paramMapping).forEach(([urlKey, dataKey]) => {
    const value = data[dataKey]
    if (value !== undefined && value !== null && value !== '') {
      parts.push(`${urlKey}=${encodeURIComponent(value)}`)
    }
  })

  const queryString = parts.join('&')
  if (!queryString) return url

  let result = url
  const hashIndex = result.indexOf('#')
  if (hashIndex !== -1) {
    const beforeHash = result.slice(0, hashIndex)
    const hash = result.slice(hashIndex)
    const separator = beforeHash.includes('?') ? '&' : '?'
    result = beforeHash + separator + queryString + hash
  } else {
    const separator = result.includes('?') ? '&' : '?'
    result += separator + queryString
  }

  return result
}


/**
 * 颜色深浅识别 - 判断背景色应使用黑色还是白色文字
 * @author: BYM<yongming.bai@outlook.com>
 * @param {string} color - 支持十六进制（#RRGGBB、#RGB、RRGGBB、RGB）、rgb(r,g,b)、rgba(r,g,b,a)、rgb(r g b) 格式
 * @returns {string} "light"（亮色背景，建议黑色文字） 或 "dark"（深色背景，建议白色文字）
 */

export function getColorShade(color) {
  let r, g, b;
  const trimmed = color.trim();

  // 匹配 rgb(...) / rgba(...)，同时支持逗号分隔和空格分隔（CSS Level 4）
  const rgbCommaMatch = trimmed.match(/^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*(?:,\s*[\d.]+\s*)?\)$/i);
  const rgbSpaceMatch = !rgbCommaMatch && trimmed.match(/^rgba?\(\s*(\d{1,3})\s+(\d{1,3})\s+(\d{1,3})\s*(?:\/\s*[\d.%]+\s*)?\)$/i);
  const rgbMatch = rgbCommaMatch || rgbSpaceMatch;

  if (rgbMatch) {
    r = parseInt(rgbMatch[1], 10);
    g = parseInt(rgbMatch[2], 10);
    b = parseInt(rgbMatch[3], 10);
  } else {
    // 十六进制处理
    let hex = trimmed.replace(/^#/, '');

    // 处理 3 位缩写，例如 "abc" 扩展为 "aabbcc"
    if (hex.length === 3) {
      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }

    // 格式校验：长度必须为 6，且只能包含十六进制字符
    if (!/^[0-9A-Fa-f]{6}$/.test(hex)) {
      console.error('无效的颜色格式，期望十六进制颜色值或 rgb/rgba 格式');
      return 'light';
    }

    r = parseInt(hex.substring(0, 2), 16);
    g = parseInt(hex.substring(2, 4), 16);
    b = parseInt(hex.substring(4, 6), 16);
  }

  // 使用 WCAG 2.0 相对亮度公式（比 BT.601 更符合人眼感知）
  // 先将 sRGB 值转换为线性 RGB，再加权求和
  const toLinear = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const luminance = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);

  // 阈值 0.179：基于 WCAG 对比度标准，低于此值白色文字可读性更好，高于此值黑色文字可读性更好
  return luminance > 0.179 ? 'light' : 'dark';
}
